import { isCopperLayer, isMiddleCopperLayer, isOverlayLayer, isSolderMaskLayer, isSolderPasteLayer, matchLayerIdentifier, PcbNodeTypes, } from "@buildwithflux/models";
import { kicad } from "@buildwithflux/pcb-converters";
import { metersToMm } from "@buildwithflux/shared";
import { getFirstTopLevelLayout, isNodeInTree } from "../../../nodes";
import { getSubstrateThickness, LayerOrientation } from "../../../pcb";
import { nodeMappers } from "./mappers";
const NODE_MAPPING_PRIORITIES = [
    // these are the nodes that must be converted/mapped first as other nodes depend on them
    PcbNodeTypes.net,
    PcbNodeTypes.element,
];
const getNodeMappingPriority = (type) => {
    return NODE_MAPPING_PRIORITIES.includes(type) ? NODE_MAPPING_PRIORITIES.indexOf(type) : Infinity;
};
export class KiCadConverter {
    nodes;
    logger;
    fillShapes;
    tracingGraph;
    _context;
    _innerCopperLayerCount = 0;
    _logger;
    constructor(nodes, logger, fillShapes, tracingGraph) {
        this.nodes = nodes;
        this.logger = logger;
        this.fillShapes = fillShapes;
        this.tracingGraph = tracingGraph;
        const layerNamesForLayout = {};
        this._logger = logger;
        this._context = {
            tracingGraph,
            getKiCadLayerByLayerIdentifier: (uid, layerIdentifier) => {
                const footprintOrLayoutUid = nodes[uid]?.bakedRules.rootFootprintOrLayoutUid;
                if (!footprintOrLayoutUid) {
                    throw new Error(`Cannot find root footprint or layout for node ${uid}`);
                }
                const footprintOrLayout = nodes[footprintOrLayoutUid];
                if (footprintOrLayout?.type !== PcbNodeTypes.layout &&
                    footprintOrLayout?.type !== PcbNodeTypes.footprint) {
                    throw new Error(`Cannot find root layout for node ${uid}`);
                }
                const layerNames = layerNamesForLayout[footprintOrLayout.uid];
                if (!layerNames) {
                    layerNamesForLayout[footprintOrLayout.uid] = Object.values(footprintOrLayout.bakedRules?.stackup ?? {})
                        .sort((a, b) => (a.order > b.order ? 1 : -1))
                        .map((layer) => {
                        if (isCopperLayer(layer) && layer.orientation === LayerOrientation.top) {
                            return { layer, name: "F.Cu" };
                        }
                        if (isCopperLayer(layer) && layer.orientation === LayerOrientation.bottom) {
                            return { layer, name: "B.Cu" };
                        }
                        if (isMiddleCopperLayer(layer)) {
                            this._innerCopperLayerCount++;
                            if (this._innerCopperLayerCount > 30) {
                                throw new Error("KiCad only supports up to 30 middle layers");
                            }
                            return {
                                layer,
                                name: `In${this._innerCopperLayerCount}.Cu`,
                            };
                        }
                        /* todo: replace the name-based approach for non-copper nodes with these [wip-kicad-export]
                        if (isSolderMaskLayer(layer)) {
                            if (layer.orientation === LayerOrientation.top) return {layer, name: `F.Mask`};
                            if (layer.orientation === LayerOrientation.bottom) return {layer, name: `B.Mask`};
                        }

                        if (isSolderPasteLayer(layer)) {
                            if (layer.orientation === LayerOrientation.top) return {layer, name: `F.Paste`};
                            if (layer.orientation === LayerOrientation.bottom) return {layer, name: `B.Paste`};
                        }

                        if (isOverlayLayer(layer)) {
                            if (layer.orientation === LayerOrientation.top) return {layer, name: `F.SilkS`};
                            if (layer.orientation === LayerOrientation.bottom) return {layer, name: `B.SilkS`};
                        }
                        */
                        return null;
                    });
                }
                const layer = layerNamesForLayout[footprintOrLayout.uid]?.find((l) => {
                    if (l?.layer) {
                        return matchLayerIdentifier(l.layer, layerIdentifier);
                    }
                    else {
                        return null;
                    }
                });
                if (!layer) {
                    if (layerIdentifier === "Top")
                        return "F.Cu";
                    if (layerIdentifier === "Bottom")
                        return "B.Cu";
                    this._logger.warn(`Cannot find the layer named "${layerIdentifier}" in the stackup for node ${uid}`);
                    return null;
                }
                return layer.name;
            },
            fillShapes,
            rootModules: {
                top: { layer: "F.Cu", name: "ROOT.top" },
                bottom: { layer: "B.Cu", name: "ROOT.bottom" },
            },
            nodes: {},
        };
    }
    convert() {
        const { nodesByType, postprocessors } = this._convertNodes();
        const modules = [this._context.rootModules.top, this._context.rootModules.bottom];
        const grPoly = [];
        const nets = Object.entries(nodesByType(PcbNodeTypes.net)).map(([, net]) => net);
        const segments = Object.entries(nodesByType(PcbNodeTypes.routeSegment))
            .map(([, segment]) => segment)
            .flat();
        const vias = Object.entries(nodesByType(PcbNodeTypes.via)).map(([, via]) => via);
        const fillZones = Object.entries(nodesByType(PcbNodeTypes.fill))
            .map(([, zone]) => zone)
            .flat();
        const zones = fillZones;
        const layers = [
            { name: "F.Cu", layer_type: "signal", hide: false },
            ...new Array(this._innerCopperLayerCount).fill(0).map((_, innerLayerIndex) => ({
                name: `In${innerLayerIndex + 1}.Cu`,
                layer_type: "signal",
                hide: false,
            })),
            // todo: do we need all of the following layers? [wip-kicad-export]
            { name: "B.Cu", layer_type: "signal", hide: false },
            { name: "B.Adhes", layer_type: "user", hide: false },
            { name: "F.Adhes", layer_type: "user", hide: false },
            { name: "B.Paste", layer_type: "user", hide: false },
            { name: "F.Paste", layer_type: "user", hide: false },
            { name: "B.SilkS", layer_type: "user", hide: false },
            { name: "F.SilkS", layer_type: "user", hide: false },
            { name: "B.Mask", layer_type: "user", hide: false },
            { name: "F.Mask", layer_type: "user", hide: false },
            { name: "Dwgs.User", layer_type: "user", hide: false },
            // NOTE: Cmts.User are disabled downstream. See FootprintVirtualNodeGenerator.
            { name: "Cmts.User", layer_type: "user", hide: false },
            { name: "Eco1.User", layer_type: "user", hide: false },
            { name: "Eco2.User", layer_type: "user", hide: false },
            { name: "Edge.Cuts", layer_type: "user", hide: false },
            { name: "Margin", layer_type: "user", hide: false },
            { name: "B.CrtYd", layer_type: "user", hide: false },
            { name: "F.CrtYd", layer_type: "user", hide: false },
            { name: "B.Fab", layer_type: "user", hide: false },
            { name: "F.Fab", layer_type: "user", hide: false },
        ];
        const boardThickness = this._getBoardThickness();
        // todo: verify what needs changing here (copied from the original KiCad implementation template) [wip-kicad-export]
        const board = {
            version: 20171130,
            page: {
                size: "A4",
            },
            module: modules,
            general: {
                thickness: boardThickness,
                drawings: 0,
                tracks: vias.length + segments.length, // todo: add arcs.length [wip-kicad-export]
                zones: zones.length,
                modules: modules.length,
                nets: nets.length,
            },
            layers: layers.map((layer, index) => ({ ...layer, index: index.toString() })),
            net: nets,
            gr_poly: grPoly,
            net_class: [],
            segment: segments,
            via: vias,
            setup: {
                last_trace_width: 0.25,
                trace_clearance: 0.2,
                zone_clearance: 0.508,
                zone_45_only: false,
                trace_min: 0.2,
                via_size: 0.8,
                via_drill: 0.4,
                via_min_size: 0.4,
                via_min_drill: 0.3,
                uvia_size: 0.3,
                uvia_drill: 0.1,
                uvias_allowed: true,
                uvia_min_size: 0.2,
                uvia_min_drill: 0.1,
                blind_buried_vias_allowed: true,
                edge_width: 0.05,
                segment_width: 0.2,
                pcb_text_width: 0.3,
                pcb_text_size: [1.5, 1.5],
                mod_edge_width: 0.12,
                mod_text_size: [1, 1],
                mod_text_width: 0.15,
                pad_size: [1.7, 3.4],
                pad_drill: 1,
                pad_to_mask_clearance: 0,
                aux_axis_origin: [136, 99],
                grid_origin: [136, 99],
                visible_elements: "FFFFFF7F",
                pcbplotparams: {},
            },
        };
        if (zones.length) {
            board.zone = zones;
        }
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        postprocessors.forEach(({ mappedNode, postprocess }) => {
            postprocess(mappedNode ?? null, board);
        });
        const kicadString = kicad.serializeBoard(board).toString();
        return {
            board,
            kicadString: kicadString,
            layersForGerberExport: this._getLayersForGerberExport(),
        };
    }
    _convertNodes() {
        this._innerCopperLayerCount = 0;
        this._context.nodes = {};
        const postprocessors = [];
        const mappedResults = Object.entries(this.nodes)
            .filter(([uid]) => isNodeInTree(this.nodes, uid))
            .sort(([, a], [, b]) => (getNodeMappingPriority(a.type) < getNodeMappingPriority(b.type) ? -1 : 1))
            .reduce((nodes, [uid, node]) => {
            const nodePostprocessors = [];
            const mapper = nodeMappers[node.type];
            const mappedNode = mapper?.(node, this._context, (p) => nodePostprocessors.push(p), this.nodes);
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            nodePostprocessors.forEach((p) => {
                const nodesToPostProcess = Array.isArray(mappedNode) ? mappedNode : [mappedNode];
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                nodesToPostProcess.forEach((n) => postprocessors.push({ postprocess: p, mappedNode: n }));
            });
            const nodesOfType = nodes[node.type] ?? (nodes[node.type] = {});
            if (mappedNode) {
                nodesOfType[uid] = mappedNode;
                const contextNodesOfType = this._context.nodes[node.type] ?? (this._context.nodes[node.type] = {});
                contextNodesOfType[uid] = mappedNode;
            }
            return nodes;
        }, {});
        return {
            mappedResults,
            postprocessors,
            nodesByType(type) {
                return mappedResults[type] ?? {};
            },
        };
    }
    _getBoardThickness() {
        const defaultThickness = 1.6; // Default fallback value in mm
        try {
            const layout = getFirstTopLevelLayout(this.nodes);
            if (layout && layout.type === PcbNodeTypes.layout && layout.bakedRules?.stackup) {
                const stackup = layout.bakedRules.stackup;
                const layers = Object.values(stackup);
                const thicknessInMeters = getSubstrateThickness(layers);
                const thicknessInMm = metersToMm(thicknessInMeters);
                if (thicknessInMm > 0) {
                    return thicknessInMm;
                }
            }
        }
        catch (error) {
            this._logger.warn(`Failed to calculate board thickness, using default value: ${error}`);
        }
        return defaultThickness;
    }
    _getLayersForGerberExport() {
        // TODO: should this be the first layout or the default layout???
        const layout = getFirstTopLevelLayout(this.nodes);
        if (!layout || layout.type !== PcbNodeTypes.layout) {
            throw new Error(`Cannot find a layout to export.`);
        }
        const stackup = layout.bakedRules?.stackup;
        const layers = Object.entries(stackup).map(([, layer]) => layer);
        const selection = {
            b_cu: false,
            b_mask: false,
            b_paste: false,
            b_silks: false,
            f_cu: false,
            f_mask: false,
            f_paste: false,
            f_silks: false,
        };
        for (const layer of layers) {
            selection.b_cu = selection.b_cu || (isCopperLayer(layer) && layer.orientation === LayerOrientation.bottom);
            selection.b_mask = selection.b_mask || isSolderMaskLayer(layer, LayerOrientation.bottom);
            selection.b_paste = selection.b_paste || isSolderPasteLayer(layer, LayerOrientation.bottom);
            selection.b_silks = selection.b_silks || isOverlayLayer(layer, LayerOrientation.bottom);
            selection.f_cu = selection.f_cu || (isCopperLayer(layer) && layer.orientation === LayerOrientation.top);
            selection.f_mask = selection.f_mask || isSolderMaskLayer(layer, LayerOrientation.top);
            selection.f_paste = selection.f_paste || isSolderPasteLayer(layer, LayerOrientation.top);
            selection.f_silks = selection.f_silks || isOverlayLayer(layer, LayerOrientation.top);
        }
        const selectedLayers = Object.entries(selection)
            .filter(([, selected]) => selected)
            .map(([layer]) => layer);
        const innerCopperLayers = new Array(this._innerCopperLayerCount).fill(0).map((_, index) => `in${index + 1}_cu`);
        return ["drill", "edge_cuts", ...selectedLayers, ...innerCopperLayers];
    }
}
//# sourceMappingURL=KicadConverter.js.map